/**
 * Web component for inline help content rendered in an ABR-owned modal.
 *
 * Usage:
 *   <anear-help label="Question help" title="Question Tips">
 *     <h3>Question tips</h3>
 *     <p>Use playful but fair answers.</p>
 *   </anear-help>
 *
 * The component captures its initial inner HTML as modal content, then replaces
 * its inline DOM with a standard question-mark trigger.
 */
class AnearHelp extends HTMLElement {
  constructor() {
    super()
    this._label = null
    this._title = null
    this._iconStyle = null
    this._onHelpOpen = null
    this._boundClickHandler = null
    this._contentHtml = ''
    this._hasCapturedInitialContent = false
  }

  static get observedAttributes() {
    return ['label', 'title', 'icon-style']
  }

  connectedCallback() {
    this.captureInitialContent()
    this.render()
    this.updateClickHandler()
  }

  disconnectedCallback() {
    if (this._boundClickHandler) {
      this.removeEventListener('click', this._boundClickHandler, false)
      this._boundClickHandler = null
    }
  }

  attributeChangedCallback(name, _oldValue, newValue) {
    if (name === 'label') this._label = newValue
    if (name === 'title') this._title = newValue
    if (name === 'icon-style') this._iconStyle = newValue
    if (!this.isConnected || !this._hasCapturedInitialContent) return
    this.render()
    this.updateClickHandler()
  }

  initialize(onHelpOpen) {
    this._onHelpOpen = onHelpOpen
    this.updateClickHandler()
  }

  captureInitialContent() {
    if (this._hasCapturedInitialContent) return
    this._contentHtml = this.innerHTML.trim()
    this._hasCapturedInitialContent = true
  }

  get label() {
    return this.getAttribute('label') || this._label || this.title || 'Open help'
  }

  get title() {
    return this.getAttribute('title') || this._title || this.getAttribute('label') || 'Help'
  }

  get iconStyle() {
    return this.getAttribute('icon-style') || this._iconStyle || 'outline'
  }

  get helpHtml() {
    return this._contentHtml || ''
  }

  get theme() {
    const styles = window.getComputedStyle(this)
    return {
      headerBackground: styles.getPropertyValue('--anear-help-header-bg').trim(),
      frameBackground: styles.getPropertyValue('--anear-help-frame-bg').trim(),
      bodyBackground: styles.getPropertyValue('--anear-help-body-bg').trim(),
      titleColor: styles.getPropertyValue('--anear-help-title-color').trim(),
      titleFontSize: styles.getPropertyValue('--anear-help-title-size').trim(),
      titleFontFamily: styles.getPropertyValue('--anear-help-title-font-family').trim(),
      closeColor: styles.getPropertyValue('--anear-help-close-color').trim(),
    }
  }

  render() {
    const label = this.label
    const iconStyle = this.iconStyle
    this.innerHTML = `
      <button
        type="button"
        class="anear-help-trigger"
        data-icon-style="${iconStyle}"
        aria-label="${this.escapeAttribute(label)}"
        title="${this.escapeAttribute(label)}"
      >?</button>
    `
  }

  updateClickHandler() {
    if (this._boundClickHandler) {
      this.removeEventListener('click', this._boundClickHandler, false)
      this._boundClickHandler = null
    }

    const shouldHandleClick = typeof this._onHelpOpen === 'function' && Boolean(this.helpHtml)
    if (!shouldHandleClick) return

    this._boundClickHandler = (event) => {
      const trigger = event.target.closest('.anear-help-trigger')
      if (!trigger) return

      event.preventDefault?.()
      event.stopPropagation?.()

      this._onHelpOpen({
        label: this.label,
        title: this.title,
        html: this.helpHtml,
        iconStyle: this.iconStyle,
        theme: this.theme,
      })
    }

    this.addEventListener('click', this._boundClickHandler)
  }

  escapeAttribute(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
  }
}

if (!customElements.get('anear-help')) {
  customElements.define('anear-help', AnearHelp)
}

export default AnearHelp
